import { Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives/responsive";

import { collectionRelatedSectionContentfulRelayFragment$key } from "@/__generated_contentful__/collectionRelatedSectionContentfulRelayFragment.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

import { CollectionCardRelay as CollectionCard } from "../../components/contentful-relay-components/collection-card-contentful-relay";

interface CollectionRelatedSectionProps {
    collections: collectionRelatedSectionContentfulRelayFragment$key;
}

const sectionStyle = css({
    paddingBlockStart: 96
});
const headingStyle = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    margin: 0,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});
const collectionsBox = css({
    paddingBlockStart: 32,
    display: "flex",
    gap: 32,
    flexWrap: "wrap",

    [media.below.sm]: {
        gap: 16
    }
});
const hrefStyle = css({
    "color": "#0052cc",
    "fontWeight": 500,
    "fontSize": "1rem",
    "lineHeight": "1.25rem",
    "textDecoration": "underline",
    "marginInlineStart": 16,

    ":hover": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0065ff",
        textDecoration: "none"
    },
    ":active": {
        textDecoration: "none"
    },
    ":focus": {
        textDecoration: "none"
    },
    ":visited": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0052cc"
    }
});

const backWrapper = css({
    textAlign: "center",
    paddingBlockStart: 96,
    paddingBlockEnd: 96
});

const CollectionRelatedSectionRelayFragment = graphql`
    fragment collectionRelatedSectionContentfulRelayFragment on TemplateCollection
    @relay(plural: true) {
        sys {
            id
        }
        ...collectionCardContentfulRelayFragment
    }
`;

export const CollectionRelatedSection = ({
    collections
}: CollectionRelatedSectionProps) => {
    const data = useFragment(
        CollectionRelatedSectionRelayFragment,
        collections
    );

    const viewAllHref = useLocalizedHref("/software/confluence/templates");

    if (!data || !Array.isArray(data)) {
        return null;
    }

    const firstTwoCollections = data.slice(0, 2);
    if (firstTwoCollections.length === 0) {
        return null;
    }
    return (
        <Fragment>
            <section
                css={sectionStyle}
                data-testid="confluence-template-collection-related"
            >
                {firstTwoCollections && firstTwoCollections.length > 0 && (
                    <Fragment>
                        <h3 css={headingStyle}>
                            <FormattedMessage
                                {...ConfluenceTemplateMessages.moreCollections}
                            />
                            <a
                                href={viewAllHref}
                                css={hrefStyle}
                                {...ConfluenceTemplateAnalytics.viewAllCollections()}
                            >
                                <FormattedMessage
                                    {...ConfluenceTemplateMessages.viewAllCollections}
                                />
                            </a>
                        </h3>
                        <div css={collectionsBox}>
                            {firstTwoCollections.map((collection) => (
                                <CollectionCard
                                    key={collection.sys.id}
                                    collection={collection}
                                />
                            ))}
                        </div>
                    </Fragment>
                )}
            </section>
            <div css={backWrapper}>
                <a
                    href={viewAllHref}
                    css={hrefStyle}
                    {...ConfluenceTemplateAnalytics.backToGalleryAnalyticProps()}
                >
                    <FormattedMessage
                        {...ConfluenceTemplateMessages.backToGallery}
                    />
                </a>
            </div>
        </Fragment>
    );
};
